import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { BRAND, type Locale } from "@xiaotian-vault/shared";

export const translations = {
  "zh-CN": {
    brand: BRAND.zh,
    brandSub: "",
    language: "中文 / EN",
    admin: "文件管理",
    adminSubtitle: "上传文件、创建分享链接，并查看下载情况。",
    username: "用户名",
    password: "密码",
    totp: "Google Authenticator 验证码",
    signIn: "登录管理",
    signOut: "退出",
    upload: "上传文件",
    dropFile: "拖拽文件到这里，或点击选择",
    uploadHint: "支持超大文件，上传中断后可继续。",
    uploading: "上传中",
    pause: "暂停",
    resume: "继续",
    files: "文件",
    noFiles: "还没有文件。上传第一个文件吧。",
    createShare: "创建分享",
    expiry: "过期时间",
    noExpiry: "永不过期",
    enabled: "已启用",
    disabled: "已停用",
    disable: "停用",
    enable: "启用",
    delete: "删除",
    regenerate: "重置提取码",
    copy: "复制链接",
    passkey: "提取码",
    newPasskey: "新的 4 位提取码",
    copied: "已复制",
    copyFailed: "复制失败，请手动复制。",
    downloads: "下载次数",
    lastDownload: "最后下载",
    shareCreated: "分享已创建",
    passkeyCopied: "提取码已复制",
    linkCopied: "链接已复制",
    shareDetailsCopied: "链接和提取码已复制",
    passkeyUnavailable: "旧链接无法显示提取码，请先重置提取码。",
    confirmDelete: "确认删除",
    cancel: "取消",
    uploaded: "已上传",
    speed: "速度",
    eta: "预计剩余",
    sealedTitle: "链接不可用",
    sealedSubtitle: "这个链接可能已过期、停用，或者并不存在。",
    disabledShare: "分享已被停用。",
    tooManyAttempts: "尝试次数过多，请稍后再试。",
    codeAccepted: "提取码正确",
    preview: "预览",
    readyToDownload: "可以下载",
    publicTitle: "文件已准备好",
    publicSubtitle: "输入 4 位提取码，查看这个分享文件。",
    enterPasskey: "输入提取码",
    unlock: "查看文件",
    unlocking: "正在打开",
    ready: "可以下载了",
    download: "下载文件",
    grantHint: "只要分享保持启用，下载链接就可以继续使用。",
    loading: "正在加载分享",
    unavailable: "这个分享暂时不可用。",
    wrongPasskey: "提取码不正确。",
    expired: "分享已过期。",
    homeTitle: "小田田仓库",
    homeSubtitle: "简单好用的文件分享入口。",
    openAdmin: "打开管理后台",
    fileSize: "文件大小",
    fileType: "文件类型",
    expires: "过期",
    never: "永不",
    status: "状态",
    shareLink: "分享链接",
    publicTheme: "公开链接主题",
    themeCinematic: "电影金库",
    themeCyber: "赛博传输",
    themeSavedState: "已同步",
    themeActive: "生效中",
    themeSelected: "已选择",
    savingTheme: "正在保存",
    themeSaved: "主题已保存",
    themeSaveFailed: "主题保存失败，请重试。",
  },
  "en-US": {
    brand: BRAND.en,
    brandSub: "",
    language: "中文 / EN",
    admin: "File Manager",
    adminSubtitle:
      "Upload files, create share links, and keep downloads organized.",
    username: "Username",
    password: "Password",
    totp: "Google Authenticator code",
    signIn: "Sign in",
    signOut: "Sign out",
    upload: "Upload file",
    dropFile: "Drop a file here, or click to choose",
    uploadHint: "Built for huge files with resumable uploads.",
    uploading: "Uploading",
    pause: "Pause",
    resume: "Resume",
    files: "Files",
    noFiles: "No files yet. Upload your first file.",
    createShare: "Create share",
    expiry: "Expiry",
    noExpiry: "No expiry",
    enabled: "Enabled",
    disabled: "Disabled",
    disable: "Disable",
    enable: "Enable",
    delete: "Delete",
    regenerate: "Reset code",
    copy: "Copy link",
    passkey: "Code",
    newPasskey: "New 4-digit code",
    copied: "Copied",
    copyFailed: "Copy failed. Please copy manually.",
    downloads: "Downloads",
    lastDownload: "Last download",
    shareCreated: "Share created",
    passkeyCopied: "Code copied",
    linkCopied: "Link copied",
    shareDetailsCopied: "Link and code copied",
    passkeyUnavailable: "Old links cannot show the code. Reset the code first.",
    confirmDelete: "Confirm delete",
    cancel: "Cancel",
    uploaded: "Uploaded",
    speed: "Speed",
    eta: "ETA",
    sealedTitle: "Link unavailable",
    sealedSubtitle: "The link may be expired, disabled, or no longer exist.",
    disabledShare: "This share has been disabled.",
    tooManyAttempts: "Too many attempts. Please wait and try again.",
    codeAccepted: "Code accepted",
    preview: "Preview",
    readyToDownload: "Ready to download",
    publicTitle: "Your file is ready",
    publicSubtitle: "Enter the 4-digit code to view this shared file.",
    enterPasskey: "Enter code",
    unlock: "View file",
    unlocking: "Opening",
    ready: "Ready to download",
    download: "Download file",
    grantHint: "The download link stays active while this share is enabled.",
    loading: "Loading share",
    unavailable: "This share is not available.",
    wrongPasskey: "The code is incorrect.",
    expired: "This share has expired.",
    homeTitle: "Elliot's Secret Vault",
    homeSubtitle: "A simple place to share files.",
    openAdmin: "Open admin",
    fileSize: "File size",
    fileType: "File type",
    expires: "Expires",
    never: "Never",
    status: "Status",
    shareLink: "Share link",
    publicTheme: "Public link theme",
    themeCinematic: "Cinematic Vault",
    themeCyber: "Cyber Transfer",
    themeSavedState: "Synced",
    themeActive: "Active",
    themeSelected: "Selected",
    savingTheme: "Saving",
    themeSaved: "Theme saved",
    themeSaveFailed: "Theme save failed. Please try again.",
  },
} as const;

export type TranslationKey = keyof (typeof translations)["en-US"];

type LocaleContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey) => string;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function localeFromLanguage(language: string): Locale {
  return language.toLowerCase().startsWith("zh") ? "zh-CN" : "en-US";
}

export function localeFromUrlSearch(search: string): Locale | null {
  const params = new URLSearchParams(search);
  const value = params.get("lang") ?? params.get("locale");
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === "zh" || normalized === "zh-cn" || normalized === "cn") {
    return "zh-CN";
  }
  if (normalized === "en" || normalized === "en-us") {
    return "en-US";
  }
  return null;
}

export function localeToUrlLang(locale: Locale): "zh" | "en" {
  return locale === "zh-CN" ? "zh" : "en";
}

function updateLocationLocale(locale: Locale) {
  const url = new URL(window.location.href);
  url.searchParams.set("lang", localeToUrlLang(locale));
  const next = `${url.pathname}${url.search}${url.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) window.history.pushState(null, "", next);
}

function storedLocale(): Locale | null {
  const saved = window.localStorage.getItem("vault-locale");
  return saved === "zh-CN" || saved === "en-US" ? saved : null;
}

function preferredLocale(): Locale {
  return (
    localeFromUrlSearch(window.location.search) ??
    storedLocale() ??
    localeFromLanguage(window.navigator.language || "en-US")
  );
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => preferredLocale());

  const value = useMemo<LocaleContextValue>(() => {
    return {
      locale,
      setLocale: (next) => {
        window.localStorage.setItem("vault-locale", next);
        updateLocationLocale(next);
        setLocaleState(next);
      },
      t: (key) => translations[locale][key],
    };
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const syncLocaleFromUrl = () => {
      setLocaleState(preferredLocale());
    };

    window.addEventListener("popstate", syncLocaleFromUrl);
    return () => window.removeEventListener("popstate", syncLocaleFromUrl);
  }, []);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useI18n(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("LocaleProvider is missing");
  return value;
}
